// SQL 03 · Database Fundamentals — graded practice bank.
//
// sql-03-database-fundamentals-guide.md teaches the object stack underneath
// the model SQL 02 already built: schemas, views vs materialized views,
// indexes, constraints, and transactions/isolation. These five cases walk
// that same stack on the same Northstar data, one decision per object type.
//
// Every table is real data from /data/datasets/table-detective-retail-export.csv
// and every figure is computed from it: 15 rows, 10 orders, 6 customers,
// 4 stores, 6 products, 1,985.75 total.

export const SQL03_DATABASE_FUNDAMENTALS_TRACKER_ID = 'sql-03-database-fundamentals-practice-bank';

export const SQL03_DATABASE_FUNDAMENTALS_LENSES = [
  ['decision', 'What to build'],
  ['definition', 'Definition / DDL'],
  ['rule', 'The rule or guarantee'],
  ['evidence', 'Evidence in the data'],
  ['tradeoff', 'Cost / trade-off'],
  ['caution', 'Risk if you get it wrong'],
];

export const SQL03_DATABASE_FUNDAMENTALS_ITEMS = [
  {
    id: 'DF01',
    title: 'DF 01: Two teams, one table name',
    scenario: "Northstar's sales team owns a Customers table feeding the till (customer_id, customer_name, customer_region, customer_segment). The new loyalty programme team wants their own Customers table holding points_balance and tier, refreshed nightly from a third-party vendor with its own customer numbering. A junior proposes naming the second table Customers2 so it doesn't collide. Decide how to structure this.",
    tables: [{
      columns: ['Table', 'Owner', 'Refresh pattern', 'Example row'],
      rows: [
        ['Customers (sales)', 'Till / sales team', 'Live, written on every checkout', 'C-042 Amara Okafor, North'],
        ['Customers (loyalty)', 'Loyalty team', 'Nightly batch from a vendor feed', 'points_balance, tier'],
      ],
    }],
    answerGuide: {
      decision: "Two schemas, not a renamed table: sales.Customers (till-owned, canonical customer identity) and loyalty.Customers (loyalty-owned, refreshed nightly). A suffix like Customers2 hides the ownership difference instead of expressing it.",
      definition: "CREATE SCHEMA sales; CREATE SCHEMA loyalty; CREATE TABLE sales.Customers(...); CREATE TABLE loyalty.Customers(...) — a schema is a namespace that lets two teams each own an object called Customers without collision.",
      rule: "Every unqualified table reference resolves against the connecting user's default schema, so sales.Customers and loyalty.Customers coexist safely as long as every cross-schema query is fully qualified.",
      evidence: "Amara Okafor is C-042 in sales.Customers, used on orders O-1001 and O-1004. The loyalty vendor may key the same person on its own ID entirely, so joining the two tables needs an explicit mapping, not an assumption that the IDs match.",
      tradeoff: "Splitting by schema costs an extra qualifier on every cross-schema join and a small amount of governance overhead — deciding who can grant access to which schema — in exchange for each team owning, permissioning and refreshing their table independently.",
      caution: "Customers2 gives no signal about which table is authoritative for identity, who refreshes it, or whether the two customer_id values even mean the same thing — the first analyst to join them on customer_id without checking will silently mismatch loyalty and sales customers.",
    },
    ruleTerms: {
      decision: ['schema', 'sales.customers', 'loyalty.customers'],
      definition: ['create schema', 'namespace'],
      rule: ['unqualified', 'default schema', 'qualified'],
      evidence: ['c-042', 'amara', 'vendor'],
      tradeoff: ['qualifier', 'governance', 'own'],
      caution: ['customers2', 'authoritative', 'mismatch'],
    },
    criticalTerms: { decision: ['schema'], evidence: ['C-042'] },
  },
  {
    id: 'DF02',
    title: 'DF 02: The Monday morning dashboard',
    scenario: "The board's category-revenue dashboard is currently a regular view joining OrderItems and Products with a GROUP BY. It only gets looked at once a week, on Monday morning, but the underlying join is getting slower as order volume grows. Decide whether to keep it as a view or change its object type.",
    tables: [{
      columns: ['product_category', 'revenue'],
      rows: [
        ['Furniture', '926.60'],
        ['Electronics', '543.75'],
        ['Accessories', '515.40'],
      ],
    }],
    answerGuide: {
      decision: "Convert vw_CategoryRevenue into a materialized view, refreshed once a week just before the Monday meeting, not a regular view.",
      definition: "CREATE MATERIALIZED VIEW reporting.mv_CategoryRevenue AS SELECT p.product_category, SUM(oi.line_total) AS revenue FROM OrderItems oi JOIN Products p ON p.product_id = oi.product_id GROUP BY p.product_category; then REFRESH MATERIALIZED VIEW reporting.mv_CategoryRevenue; on a schedule.",
      rule: "A materialized view stores its result physically and only changes when explicitly refreshed; a regular view re-runs the underlying join and GROUP BY over OrderItems and Products every single time anyone opens the dashboard.",
      evidence: "Category revenue is Furniture 926.60, Electronics 543.75, Accessories 515.40, totalling 1,985.75 — the board only needs this number as of Monday morning, not live to the second, and Northstar's real order volume will be far larger than this 15-line export.",
      tradeoff: "Materializing makes the dashboard fast to open but lets it drift up to a week stale if the refresh schedule slips; a regular view is always accurate but pays the full join-plus-aggregate cost on every single open.",
      caution: "Forgetting to schedule the REFRESH is the classic failure mode — the dashboard shows no errors, it just silently keeps last Monday's numbers indefinitely, which is worse than being slow because nobody notices until finance flags a discrepancy.",
    },
    ruleTerms: {
      decision: ['materialized view', 'not a regular view'],
      definition: ['create materialized view', 'refresh'],
      rule: ['stores', 'physically', 're-runs'],
      evidence: ['926.60', 'furniture', '1,985.75'],
      tradeoff: ['stale', 'fast', 'accurate'],
      caution: ['forgetting', 'silently', 'discrepancy'],
    },
    criticalTerms: { rule: ['refresh'], evidence: ['926.60'] },
  },
  {
    id: 'DF03',
    title: 'DF 03: The product page lookup',
    scenario: "The product detail page runs SELECT * FROM OrderItems WHERE product_id = 'P-100' every time someone views the Wireless Mouse page, to show \"recently ordered with this item\". OrderItems currently has no index besides its primary key on order_line_id, and it also receives a new row on every single checkout across the site. Decide what, if anything, to index.",
    tables: [{
      columns: ['order_line_id', 'order_id', 'product_id', 'product_name'],
      rows: [
        ['O-1001-1', 'O-1001', 'P-100', 'Wireless Mouse'],
        ['O-1003-1', 'O-1003', 'P-100', 'Wireless Mouse'],
        ['O-1007-1', 'O-1007', 'P-100', 'Wireless Mouse'],
      ],
    }],
    answerGuide: {
      decision: "Add a non-clustered (secondary) index on OrderItems.product_id — not a unique index, and no change to the existing clustered key on order_line_id.",
      definition: "CREATE INDEX idx_orderitems_product_id ON OrderItems(product_id); — a separate sorted structure pointing back to the matching rows, leaving order_line_id as the primary key untouched.",
      rule: "An index turns WHERE product_id = 'P-100' from a full table scan into an index seek, but the engine must also update the index on every INSERT, UPDATE or DELETE that touches that column.",
      evidence: "P-100 (Wireless Mouse) appears on three separate lines — O-1001-1, O-1003-1 and O-1007-1 — scattered across the table in insert order; without an index, finding all three means reading every row in OrderItems.",
      tradeoff: "A non-clustered index costs storage and a small write penalty on every new OrderItems insert, which happens on every checkout site-wide, in exchange for a near-instant read on this one specific lookup pattern.",
      caution: "A unique index would be wrong here, since product_id legitimately repeats across many order lines. Indexing every column \"just in case\" would slow the checkout path, which is this table's most frequent and most latency-sensitive write.",
    },
    ruleTerms: {
      decision: ['non-clustered', 'secondary index', 'not unique'],
      definition: ['create index', 'product_id'],
      rule: ['index seek', 'table scan', 'insert'],
      evidence: ['p-100', 'o-1001-1', 'o-1003-1', 'o-1007-1'],
      tradeoff: ['storage', 'write penalty', 'checkout'],
      caution: ['unique index', 'every column', 'latency'],
    },
    criticalTerms: { definition: ['CREATE INDEX'], evidence: ['P-100'] },
  },
  {
    id: 'DF04',
    title: 'DF 04: The supplier feed with a sign-flip bug',
    scenario: "A bulk import script from a new supplier feed occasionally writes negative unit_price values into Products because of a sign-flip bug upstream, which quietly corrupts revenue reports. Application-level validation already exists in the ordering app, but the import script writes directly to the database and bypasses it entirely. Decide the fix.",
    tables: [{
      columns: ['product_id', 'product_name', 'product_category', 'unit_price'],
      rows: [
        ['P-330', 'Office Chair', 'Furniture', '120.00'],
        ['P-410', 'Standing Desk', 'Furniture', '260.00'],
      ],
    }],
    answerGuide: {
      decision: "Add a CHECK constraint on Products.unit_price at the database level, not another layer of application validation.",
      definition: "ALTER TABLE Products ADD CONSTRAINT chk_price_positive CHECK (unit_price >= 0);",
      rule: "A CHECK constraint is enforced by the engine on every write, regardless of what wrote it — the bulk import script, the ordering app, or a future tool nobody has built yet.",
      evidence: "Products like Office Chair P-330 (120.00) and Standing Desk P-410 (260.00) must always price at or above zero for category totals like Furniture's 926.60 to mean anything; a single negative unit_price silently corrupts every downstream aggregate.",
      tradeoff: "The constraint adds a tiny check cost to every INSERT and UPDATE on Products, and rejects rather than silently fixing bad rows, so the import job needs error handling for the rows it rejects.",
      caution: "Fixing this only in application code protects nothing once a second write path exists — a bulk import, a script, direct SQL — because application-level validation can always be bypassed, and by the time anyone notices, the corrupted price has already reached the database.",
    },
    ruleTerms: {
      decision: ['check constraint', 'database level'],
      definition: ['alter table', 'check', 'unit_price >= 0'],
      rule: ['enforced', 'every write', 'regardless'],
      evidence: ['p-330', 'office chair', '926.60'],
      tradeoff: ['check cost', 'rejects', 'error handling'],
      caution: ['bypassed', 'second write path', 'already reached'],
    },
    criticalTerms: { definition: ['CHECK'], evidence: ['P-330'] },
  },
  {
    id: 'DF05',
    title: 'DF 05: Two agents, one order',
    scenario: "Two support agents both open order O-1005 (Dina Shah, two lines: 159.60 on P-214 and 133.00 on P-700) at the same time to process a partial refund. Agent A is refunding line O-1005-1 (P-214). Agent B is refunding line O-1005-2 (P-700). Decide the transaction and isolation approach so neither refund is lost or double-applied, and say whether both agents should be able to proceed at the same time.",
    tables: [{
      columns: ['order_line_id', 'order_id', 'product_id', 'line_total'],
      rows: [
        ['O-1005-1', 'O-1005', 'P-214', '159.60'],
        ['O-1005-2', 'O-1005', 'P-700', '133.00'],
      ],
    }],
    answerGuide: {
      decision: "Wrap each agent's refund in its own transaction, scoped to the single OrderItems row they are editing, running at the engine's default Read Committed isolation level — not a table-level or whole-order lock.",
      definition: "BEGIN TRANSACTION; UPDATE OrderItems SET line_total = 0, refund_flag = 1 WHERE order_line_id = 'O-1005-1'; COMMIT; — run independently for Agent B against O-1005-2.",
      rule: "Isolation guarantees each transaction sees a consistent view and that concurrent transactions don't corrupt each other's writes; row-level locking means Agent A locking O-1005-1 does not block Agent B's edit to O-1005-2, because they are different rows.",
      evidence: "O-1005 has two independent lines, 159.60 on P-214 and 133.00 on P-700, each with its own order_line_id — so the two refunds touch disjoint rows and have no real conflict to serialize against.",
      tradeoff: "Read Committed lets both agents work concurrently with minimal blocking, at the cost of allowing rare anomalies like non-repeatable reads if either agent re-reads the row mid-transaction; Serializable would remove that risk but add locking overhead neither refund actually needs here.",
      caution: "Locking the whole order, or the whole table, \"to be safe\" would block Agent B until Agent A commits even though they touch different lines — an unnecessary bottleneck. The real risk worth guarding against is two agents editing the SAME line concurrently, which does need a lock or isolation guarantee.",
    },
    ruleTerms: {
      decision: ['own transaction', 'row', 'read committed'],
      definition: ['begin transaction', 'update', 'commit'],
      rule: ['row-level', 'consistent view', 'different rows'],
      evidence: ['o-1005-1', 'o-1005-2', '159.60', '133.00'],
      tradeoff: ['concurrently', 'non-repeatable', 'serializable'],
      caution: ['whole order', 'bottleneck', 'same line'],
    },
    criticalTerms: { evidence: ['O-1005'], rule: ['row-level'] },
  },
];

export const SQL03_DATABASE_FUNDAMENTALS_CONFIG = {
  trackerId: SQL03_DATABASE_FUNDAMENTALS_TRACKER_ID,
  resourceKey: 'sql_03_database_fundamentals',
  rubricKey: 'database_fundamentals_stack_and_objects',
  activityScope: 'daily_concept_clinic_take_home',
  heading: 'Learner Practice: The Object Stack, on Real Data',
  description: 'One case per object type — schemas, views, indexes, constraints, transactions — each a real request with the object type deliberately not named in the title. Your work autosaves to your own practice record.',
  introAlert: 'Name the object type before you write the DDL. A CREATE statement chosen before the rule (what the object actually guarantees) is a guess, and it will be marked as one.',
  lenses: SQL03_DATABASE_FUNDAMENTALS_LENSES,
  workingField: {
    key: 'ddl_sketch',
    label: 'DDL / decision sketch',
    placeholder: "Example: CREATE SCHEMA sales; CREATE TABLE sales.Customers(customer_id PK, ...); — name the object type, then sketch the statement that creates it.",
    prompts: SQL03_DATABASE_FUNDAMENTALS_ITEMS.reduce((acc, item) => ({
      ...acc,
      [item.id]: `Sketch the DDL this case needs for ${item.title}. Name the object type first, then write the CREATE/ALTER statement.`,
    }), {}),
  },
  items: SQL03_DATABASE_FUNDAMENTALS_ITEMS,
};
